import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Logo } from './Logo';

export default function Navbar() {
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing', href: '#pricing' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="w-full px-4 md:px-6 lg:px-8 pt-3 md:pt-4">
        <div
          className={`max-w-7xl mx-auto flex items-center justify-between h-14 md:h-16 rounded-2xl px-4 md:px-6 border backdrop-blur-xl transition-all duration-500 ${
            scrolled
              ? 'bg-white/90 border-white/60 shadow-lg shadow-dark/10'
              : 'bg-white/60 border-white/50 shadow-lg shadow-dark/5'
          }`}
        >
          {/* Logo */}
          <a href="/" className="flex items-center">
            <Logo height={56} />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="px-4 py-2 rounded-full text-sm font-medium text-dark-muted transition-all hover:bg-black/5 hover:text-dark"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <a
                  href="/gallery"
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-primary hover:bg-primary-hover text-white transition-all"
                >
                  My Creatives
                </a>
                <a
                  href="/auth/profile"
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-surface text-dark border border-surface-dark/50 hover:bg-surface-dark/40 transition-all"
                >
                  Account
                </a>
                <button
                  onClick={() => signOut()}
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-surface text-dark border border-surface-dark/50 hover:bg-surface-dark/40 transition-all cursor-pointer"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <a
                  href="/auth/sign-in"
                  className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-primary hover:bg-primary-hover text-white transition-all"
                >
                  Log in
                </a>
                <a
                  href="/auth/sign-up"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary hover:bg-primary-hover text-white transition-all"
                >
                  Sign Up
                  <i className="ri-arrow-right-line text-sm" />
                </a>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-dark"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu — a floating rounded card that MATCHES the nav pill (same
          max-w + side gutters + glassmorphic blur + rounding) so it reads as a
          connected dropdown, not a detached full-width white slab. */}
      {mobileOpen && (
        <div className="md:hidden w-full px-4 pt-2">
          <div className="max-w-7xl mx-auto rounded-2xl border border-white/60 bg-white/95 backdrop-blur-xl shadow-xl px-3 py-3 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2.5 text-sm font-medium text-dark-muted hover:text-dark rounded-lg hover:bg-surface"
              >
                {link.label}
              </a>
            ))}
            <div className="pt-2 mt-1 border-t border-surface-dark/30 space-y-2">
              {user ? (
                <>
                  <a
                    href="/gallery"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-dark-muted hover:text-dark rounded-lg hover:bg-surface"
                  >
                    My Creatives
                  </a>
                  <a
                    href="/auth/profile"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-dark-muted hover:text-dark rounded-lg hover:bg-surface"
                  >
                    Account
                  </a>
                  <button
                    onClick={() => { signOut(); setMobileOpen(false); }}
                    className="block w-full text-left px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <a
                    href="/auth/sign-in"
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 text-sm font-medium text-dark-muted hover:text-dark rounded-lg hover:bg-surface"
                  >
                    Sign In
                  </a>
                  <a
                    href="/auth/sign-up"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-hover text-white text-sm font-semibold rounded-full transition-colors"
                  >
                    Start Free
                    <i className="ri-arrow-right-line text-sm" />
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}